import { useRef, useState } from 'react';

import PackTiersList from './-PackTiersList';
import { ScrollArea } from '~/primitives/scroll-area';
import Search from '~/partials/Search';
import { Filter, Plus } from 'lucide-react';
import PackFiltersModal from './-PackFilters';
import {
    createFileRoute,
    Outlet,
    stripSearchParams,
} from '@tanstack/react-router';
import { defaultPacksParams, packsParamsSchema } from './-SearchParams';
import { levelsQuery } from '~/util/queries/public/levels';
import { seo } from '~/util';
import { packsQuery } from '~/util/queries/public/other';
import { useScopes } from '~/components/Providers/AuthProvider';
import { Button } from '~/primitives/button';
import { NewPackTierSheet } from './-NewPacktierSheet';
import { useScrollIntoViewOnMobile } from '~/util/hooks/useScrollIntoViewOnMobile';
import TabSwitch from '~/partials/TabSwitch';
import { useList } from '~/util/hooks/useList';

export const Route = createFileRoute('/packs')({
    validateSearch: packsParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultPacksParams)],
    },
    loaderDeps: ({ search: { list } }) => ({ list }),
    beforeLoad: async ({
        context: { queryClient, authed },
        search: { list },
    }) => {
        return {
            packtiers: await queryClient.fetchQuery(
                packsQuery({ list, authed }),
            ),
        };
    },
    loader: async ({ context: { packtiers, queryClient }, deps: { list } }) => {
        queryClient.prefetchQuery(levelsQuery({ list }));
        return packtiers;
    },
    head: () => ({
        meta: seo({
            title: 'Packs',
        }),
    }),
    wrapInSuspense: true,
    pendingMs: 0,
    component: PackTiers,
});

function PackTiers() {
    const [filtersModalOpen, setFiltersModalOpen] = useState(false);
    const search = Route.useSearch({ select: ({ search }) => search });
    const list = useList();
    const navigate = Route.useNavigate();

    const { hasScope } = useScopes();
    const [createPackTierOpen, setCreatePackTierOpen] = useState(false);

    const infoRef = useRef<HTMLDivElement>(null);
    const scrollToInfo = useScrollIntoViewOnMobile(infoRef);

    const tiersViewportRef = useRef<HTMLDivElement>(null);

    return (
        <div
            className="flex overflow-x-scroll snap-x snap-mandatory gap-x-2 lg:flex-1 lg:overflow-x-hidden lg:snap-none h-[calc(100vh-120px)]"
            style={{ WebkitOverflowScrolling: 'touch' }}
        >
            <main className="bg-background-dark rounded-xl flex-col flex shrink-0 w-screen snap-start overflow-y-hidden lg:snap-none lg:shrink lg:basis-1/2 lg:min-w-0 lg:flex-col lg:w-max lg:flex-1 h-full lg:overflow-x-hidden">
                <div className="px-4 lg:px-12 pt-4">
                    <div className="flex flex-col lg:flex-row items-center lg:gap-8 pb-2">
                        <h1 className="text-secondary-foreground text-[3rem] font-semibold">
                            Packs
                        </h1>
                        <TabSwitch
                            options={['classic', 'platformer']}
                            current={list}
                            onChange={(list) =>
                                navigate({
                                    to: '/packs',
                                    search: (prev) => ({
                                        ...prev,
                                        list,
                                        filters: defaultPacksParams.filters,
                                    }),
                                    replace: true,
                                })
                            }
                            className="my-2"
                            size="md"
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center w-full">
                            <Search
                                initial_input={search}
                                onInputChange={(value: string) =>
                                    navigate({
                                        from: '/packs/$packid',
                                        search: (prev) => ({
                                            ...prev,
                                            search: value,
                                        }),
                                        replace: true,
                                    })
                                }
                            />
                            <Filter
                                size={32}
                                className="mx-2 transition-colors text-secondary-light hover:text-secondary-foreground cursor-pointer"
                                onClick={() => setFiltersModalOpen(true)}
                            />
                            <PackFiltersModal
                                isOpen={filtersModalOpen}
                                onClose={() => setFiltersModalOpen(false)}
                            />
                        </div>
                        {hasScope('pack_tier_modify') && (
                            <Button onClick={() => setCreatePackTierOpen(true)}>
                                <Plus className="mr-2" />
                                Create pack tier
                            </Button>
                        )}
                    </div>
                </div>
                <ScrollArea
                    className="lg:h-[calc(100vh-260px)] m-2 pt-4"
                    viewportRef={tiersViewportRef}
                >
                    <PackTiersList
                        viewportRef={tiersViewportRef}
                        onPackSelect={scrollToInfo}
                    />
                </ScrollArea>
                {hasScope('pack_tier_modify') && (
                    <NewPackTierSheet
                        open={createPackTierOpen}
                        onOpenChange={() => setCreatePackTierOpen(false)}
                    />
                )}
            </main>
            <aside
                ref={infoRef}
                className="h-full w-screen snap-start bg-background-dark rounded-xl p-4 pt-8 flex shrink-0 lg:w-max lg:snap-none lg:shrink lg:basis-1/2 lg:min-w-0 lg:flex-1"
            >
                <Outlet />
            </aside>
        </div>
    );
}
